import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Plus, Search, Filter, TrendingUp, MapPin, Home, Building2, Key } from 'lucide-react';

interface Property {
  id: string;
  address: string;
  postcode: string;
  type: 'House' | 'Apartment' | 'Commercial' | 'Land';
  owner: string;
  tenant: string;
  status: 'Active' | 'Vacant' | 'Maintenance' | 'For Sale';
  valuation: string;
  monthlyRent: string;
  bookings: number;
  nextBooking: string;
  documents: number;
}

const AdminPropertiesPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchedData, setFetchedData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request('/admin/properties', 'GET');
        if (response.success && response.data) {
          setFetchedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const itemsPerPage = 25;

  // Use fetched data instead of hardcoded properties
  const properties: Property[] = fetchedData.map((prop: any) => ({
    id: prop.id || 'N/A',
    address: prop.address || 'Unknown Address',
    postcode: prop.postcode || 'N/A',
    type: prop.type || 'Commercial',
    owner: prop.owner || 'N/A',
    tenant: prop.tenant || 'N/A',
    status: prop.status || 'Active',
    valuation: prop.valuation ? `$${prop.valuation.toLocaleString()}` : '£0',
    monthlyRent: prop.monthly_rent ? `$${prop.monthly_rent.toLocaleString()}` : '£0',
    bookings: prop.bookings || 0,
    nextBooking: prop.next_booking || 'N/A',
    documents: prop.documents || 0
  }));

  const getPropertyIcon = (type: string) => {
    switch (type) {
      case 'House':
        return <Home className="w-5 h-5" />;
      case 'Apartment':
        return <Building2 className="w-5 h-5" />;
      case 'Commercial':
        return <Building2 className="w-5 h-5" />;
      default:
        return <MapPin className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-700 border border-green-300';
      case 'Vacant':
        return 'bg-orange-100 text-orange-700 border border-orange-300';
      case 'Maintenance':
        return 'bg-gray-100 text-gray-700 border border-gray-300';
      case 'For Sale':
        return 'bg-red-100 text-red-700 border border-red-300';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTenantStatusColor = (tenant: string) => {
    if (tenant === 'Vacant') {
      return 'text-orange-600 bg-orange-50 border border-orange-200';
    }
    return 'text-green-600 bg-green-50 border border-green-200';
  };

  const totalProperties = 342;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedProperties = properties.slice(startIdx, startIdx + itemsPerPage);

  // Handler: Create property
  const handleCreateProperty = async (propertyData: any) => {
    try {
      const response = await apiService.request('/admin/properties/create', 'POST', {
        ...propertyData,
        createdBy: localStorage.getItem('adminId'),
        createdAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Property created');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Edit property
  const handleEditProperty = async (propertyId: string, propertyData: any) => {
    try {
      const response = await apiService.request(`/admin/properties/${propertyId}/edit`, 'PUT', {
        ...propertyData,
        modifiedBy: localStorage.getItem('adminId'),
        modifiedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Property updated');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Delete property
  const handleDeleteProperty = async (propertyId: string) => {
    if (!window.confirm('Delete this property?')) return;
    try {
      const response = await apiService.request(`/admin/properties/${propertyId}/delete`, 'DELETE', {
        deletedBy: localStorage.getItem('adminId'),
        deletedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Property deleted');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export properties
  const handleExportProperties = async () => {
    try {
      const response = await apiService.request('/admin/properties/export', 'GET');
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `properties-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Properties exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Bulk property update
  const handleBulkPropertyUpdate = async (action: string) => {
    try {
      const response = await apiService.request(`/admin/properties/bulk-${action}`, 'POST', {
        action: action,
        performedBy: localStorage.getItem('adminId'),
        performedAt: new Date().toISOString()
      });
      if (response.success) {
        alert(`✓ Bulk ${action} complete`);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Properties Directory - Virewire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Properties Directory</h1>
                <p className="text-gray-600">Manage residential and commercial properties across the portfolio.</p>
              </div>
              <button 
                onClick={() => navigate('/admin/property-add')}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-6 flex items-center gap-2 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Property
              </button>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-5 gap-6">
              {/* Total Properties */}
              <div className="bg-white rounded-xl border-l-4 border-teal-600 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Total Properties</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">342</p>
                <p className="text-xs text-green-600 font-bold flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  +8% QoQ
                </p>
              </div>

              {/* Active */}
              <div className="bg-white rounded-xl border-l-4 border-green-600 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Active</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">298</p>
                <p className="text-xs text-gray-600">87% Occupancy</p>
              </div>

              {/* Vacant */}
              <div className="bg-white rounded-xl border-l-4 border-orange-500 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Vacant</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">28</p>
                <p className="text-xs text-orange-600 font-bold">Available</p>
              </div>

              {/* Maintenance */}
              <div className="bg-white rounded-xl border-l-4 border-gray-400 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Maintenance</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">12</p>
                <p className="text-xs text-gray-600">In Progress</p>
              </div>

              {/* For Sale */}
              <div className="bg-white rounded-xl border-l-4 border-red-600 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">For Sale</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">4</p>
                <p className="text-xs text-red-600 font-bold">Listing</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-xs">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by address, postcode, or owner..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 font-bold text-sm">
                <option>Property Type: All</option>
                <option>House</option>
                <option>Apartment</option>
                <option>Commercial</option>
              </select>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 font-bold text-sm">
                <option>Status: All</option>
                <option>Active</option>
                <option>Vacant</option>
                <option>Maintenance</option>
              </select>
              <button className="text-gray-600 hover:text-gray-900 font-bold text-sm uppercase tracking-widest">
                CLEAR
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Property</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Owner</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Tenant Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Valuation</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Monthly</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Bookings</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedProperties.map((property) => (
                    <tr key={property.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center text-teal-600">
                            {getPropertyIcon(property.type)}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{property.address}</p>
                            <p className="text-xs text-gray-600">{property.postcode}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">{property.type}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-700">{property.owner}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${getTenantStatusColor(property.tenant)}`}>
                          {property.tenant}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900 text-sm">{property.valuation}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-teal-600">{property.monthlyRent}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-gray-900">{property.bookings}</p>
                          <p className="text-xs text-gray-600">{property.nextBooking}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${getStatusColor(property.status)}`}>
                          {property.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={() => navigate(`/admin/property-detail?id=${property.id}&address=${encodeURIComponent(property.address)}`)}
                          className="text-teal-600 hover:text-teal-900 font-bold text-sm transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {startIdx + 1}-{Math.min(startIdx + itemsPerPage, totalProperties)} of {totalProperties} properties
              </span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ‹
                </button>
                {[1, 2, 3].map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg font-bold text-sm transition-colors ${
                      currentPage === page
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button className="px-3 py-2 text-gray-600 hover:text-gray-900 font-bold text-sm">…</button>
                <button className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  13
                </button>
                <button 
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminPropertiesPage;
